import { Components, darken, Theme } from '@mui/material/styles';
import type { WatchdutyThemePalette } from '../palette';

const SWITCH_WIDTH = 44;
const SWITCH_HEIGHT = 26;
const THUMB_SIZE = 22;
const SWITCH_PADDING = 2;
const TRACK_BORDER_RADIUS = 14;
const THUMB_TRANSLATE_X = SWITCH_WIDTH - THUMB_SIZE - SWITCH_PADDING * 2;
const TRACK_HOVER_DARKEN = 0.1;

const thumbColor = 'var(--mui-palette-switch-thumb-main)';

const trackHoverActive = (color: string): { backgroundColor: string } => ({
  backgroundColor: darken(color, TRACK_HOVER_DARKEN),
});

type SwitchTheme = Theme & { palette: WatchdutyThemePalette };

export const MuiSwitch: Components<Theme>['MuiSwitch'] = {
  defaultProps: {
    // Avoid MUI’s `color="primary"` checked thumb (WDUI primary.main is grey/yellow, not thumb).
    color: 'default',
  },
  styleOverrides: {
    root: {
      width: SWITCH_WIDTH,
      height: SWITCH_HEIGHT,
      padding: 0,
      overflow: 'hidden',
    },
    switchBase: ({ theme }) => {
      const { switch: switchPalette } = (theme as SwitchTheme).palette;
      const offMain = switchPalette.off.main;
      const onMain = switchPalette.on.main;

      return {
        padding: SWITCH_PADDING,
        color: thumbColor,
        '&:hover': {
          backgroundColor: 'transparent',
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
        '&:hover + .MuiSwitch-track': trackHoverActive(offMain),
        '&:active + .MuiSwitch-track': trackHoverActive(offMain),
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: 'var(--mui-palette-switch-off-main)',
        },
        '&.Mui-checked': {
          transform: `translateX(${THUMB_TRANSLATE_X}px)`,
          color: thumbColor,
          '&.MuiSwitch-colorPrimary': {
            color: thumbColor,
          },
          '&.MuiSwitch-colorSecondary': {
            color: thumbColor,
          },
          '&.MuiSwitch-colorSuccess': {
            color: thumbColor,
          },
          '&.MuiSwitch-colorPrimary + .MuiSwitch-track': {
            backgroundColor: 'var(--mui-palette-switch-on-main)',
          },
          '&.MuiSwitch-colorSecondary + .MuiSwitch-track': {
            backgroundColor: 'var(--mui-palette-switch-on-main)',
          },
          '&.MuiSwitch-colorSuccess + .MuiSwitch-track': {
            backgroundColor: 'var(--mui-palette-switch-on-main)',
          },
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:hover + .MuiSwitch-track': trackHoverActive(onMain),
          '&:active + .MuiSwitch-track': trackHoverActive(onMain),
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: 'var(--mui-palette-switch-on-main)',
          },
        },
        '&.Mui-disabled': {
          opacity: 0.5,
          '&:hover + .MuiSwitch-track': {
            backgroundColor: 'var(--mui-palette-switch-disabled-main)',
          },
          '&:active + .MuiSwitch-track': {
            backgroundColor: 'var(--mui-palette-switch-disabled-main)',
          },
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: 'var(--mui-palette-switch-disabled-main)',
          },
        },
        '&.Mui-disabled.Mui-checked': {
          '& + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: 'var(--mui-palette-switch-disabled-main)',
          },
        },
      };
    },
    thumb: {
      width: THUMB_SIZE,
      height: THUMB_SIZE,
      boxShadow: 'none',
      backgroundColor: thumbColor,
    },
    track: {
      borderRadius: TRACK_BORDER_RADIUS,
      opacity: 1,
    },
  },
};
